import React from "react";

import { Box, Grid, Stack } from "@mui/material";

import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { getSourceIcon } from "../../../helpers/leads/icon";
import { formatNumber } from "../LeadGeneralReportsPage.helpers";
import * as Styled from "../LeadGeneralReportsPage.styled";
import {
    DailyTimePoint,
    SourceBreakdownItem,
} from "../LeadGeneralReportsPage.types";

interface Props {
    timeseries: DailyTimePoint[];
    sources: SourceBreakdownItem[];
}

const formatDateLabel = (raw: string) => {
    try {
        const date = new Date(raw);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
        });
    } catch {
        return raw;
    }
};

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box
            sx={{
                background: "#ffffff",
                border: "1px solid #E2E8F0",
                borderRadius: 2,
                padding: "10px 12px",
                boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
                fontSize: 12,
            }}
        >
            <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                {formatDateLabel(label)}
            </Box>
            {payload.map((p: any) => (
                <Stack
                    key={p.dataKey}
                    direction="row"
                    gap={1}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: p.color,
                        }}
                    />
                    <Box sx={{ textTransform: "capitalize" }}>{p.dataKey}:</Box>
                    <Box sx={{ fontWeight: 600 }}>{formatNumber(p.value)}</Box>
                </Stack>
            ))}
        </Box>
    );
};

const AggregatedView: React.FC<Props> = ({ timeseries, sources }) => {
    return (
        <Stack gap={3}>
            <Styled.SectionCard elevation={0}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    mb={2}
                    gap={1.5}
                >
                    <Box>
                        <Styled.SectionTitle>
                            Leads Over Time
                        </Styled.SectionTitle>
                        <Styled.SectionSubtitle>
                            Sent vs failed trend across the selected range
                        </Styled.SectionSubtitle>
                    </Box>
                    <Stack direction="row" gap={2} alignItems="center">
                        <Stack direction="row" gap={0.5} alignItems="center">
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: "#16A34A",
                                }}
                            />
                            <Box sx={{ fontSize: 12, color: "#475569" }}>
                                Sent
                            </Box>
                        </Stack>
                        <Stack direction="row" gap={0.5} alignItems="center">
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: "#DC2626",
                                }}
                            />
                            <Box sx={{ fontSize: 12, color: "#475569" }}>
                                Failed
                            </Box>
                        </Stack>
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        width: "100%",
                        height: { xs: 240, sm: 280, md: 320 },
                    }}
                >
                    <ResponsiveContainer>
                        <AreaChart
                            data={timeseries}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient
                                    id="sentGrad"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="0%"
                                        stopColor="#16A34A"
                                        stopOpacity={0.25}
                                    />
                                    <stop
                                        offset="100%"
                                        stopColor="#16A34A"
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#F1F5F9"
                            />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatDateLabel}
                                stroke="#94A3B8"
                                fontSize={11}
                                tickLine={false}
                                axisLine={{ stroke: "#E2E8F0" }}
                                minTickGap={24}
                            />
                            <YAxis
                                stroke="#94A3B8"
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                width={32}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="sent"
                                stroke="#16A34A"
                                strokeWidth={2.5}
                                fill="url(#sentGrad)"
                            />
                            <Area
                                type="monotone"
                                dataKey="failed"
                                stroke="#DC2626"
                                strokeWidth={2}
                                strokeDasharray="5 4"
                                fill="transparent"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            </Styled.SectionCard>

            <Styled.SectionCard elevation={0}>
                <Box mb={2}>
                    <Styled.SectionTitle>Source Breakdown</Styled.SectionTitle>
                    <Styled.SectionSubtitle>
                        Platform-specific lead performance
                    </Styled.SectionSubtitle>
                </Box>
                <Grid container spacing={2}>
                    {sources.map((s) => {
                        const successPct = s.total
                            ? (s.sent / s.total) * 100
                            : 0;
                        const failPct = s.total
                            ? (s.failed / s.total) * 100
                            : 0;
                        return (
                            <Grid
                                key={s.sourceType}
                                size={{ xs: 12, sm: 6, lg: 4 }}
                            >
                                <Styled.SourceCard
                                    elevation={0}
                                    $accent={s.accentColor}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        gap={1}
                                    >
                                        <Stack
                                            direction="row"
                                            gap={1}
                                            alignItems="center"
                                        >
                                            <img
                                                src={getSourceIcon(
                                                    s.sourceType,
                                                )}
                                                alt={s.name}
                                            />
                                            <Box>
                                                <Box
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {s.name}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        fontSize: 11,
                                                        color: "#94A3B8",
                                                    }}
                                                >
                                                    {s.category}
                                                </Box>
                                            </Box>
                                        </Stack>
                                        <Box sx={{ textAlign: "right" }}>
                                            <Box
                                                sx={{
                                                    fontSize: 20,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {formatNumber(s.total)}
                                            </Box>
                                            <Box
                                                sx={{
                                                    fontSize: 11,
                                                    color: "#94A3B8",
                                                }}
                                            >
                                                Total Leads
                                            </Box>
                                        </Box>
                                    </Stack>

                                    <Stack gap={0.5}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            sx={{ fontSize: 12 }}
                                        >
                                            <Box sx={{ color: "#16A34A" }}>
                                                Sent ({formatNumber(s.sent)})
                                            </Box>
                                            <Box
                                                sx={{
                                                    color: "#16A34A",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {successPct.toFixed(0)}%
                                            </Box>
                                        </Stack>
                                        <Styled.ProgressTrack>
                                            <Styled.ProgressFill
                                                $width={successPct}
                                                $color="#16A34A"
                                            />
                                        </Styled.ProgressTrack>
                                    </Stack>

                                    <Stack gap={0.5}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            sx={{ fontSize: 12 }}
                                        >
                                            <Box sx={{ color: "#DC2626" }}>
                                                Failed ({formatNumber(s.failed)}
                                                )
                                            </Box>
                                            <Box
                                                sx={{
                                                    color: "#DC2626",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {failPct.toFixed(0)}%
                                            </Box>
                                        </Stack>
                                        <Styled.ProgressTrack>
                                            <Styled.ProgressFill
                                                $width={failPct}
                                                $color="#DC2626"
                                            />
                                        </Styled.ProgressTrack>
                                    </Stack>
                                </Styled.SourceCard>
                            </Grid>
                        );
                    })}
                </Grid>
            </Styled.SectionCard>
        </Stack>
    );
};

export default AggregatedView;
